import { supabase } from '../supabase-config';

// Comprehensive auth diagnostics utility
export const authDiagnostics = {
  // Test Supabase connection
  testConnection: async () => {
    console.log('🔍 Testing Supabase connection...');
    const startTime = Date.now();
    
    try {
      // Test basic connectivity
      const { error } = await supabase
        .from('users')
        .select('id')
        .limit(1);
      
      const responseTime = Date.now() - startTime;
      
      if (error) {
        console.error('❌ Database connection failed:', error);
        return { 
          success: false, 
          error: error.message,
          responseTime 
        };
      }
      
      console.log(`✅ Database connected (${responseTime}ms)`);
      return { 
        success: true, 
        responseTime,
        message: `Connected in ${responseTime}ms` 
      };
    } catch (err) {
      console.error('❌ Connection error:', err);
      return { 
        success: false, 
        error: err.message,
        responseTime: Date.now() - startTime 
      };
    }
  },

  // Test auth session
  testAuthSession: async () => {
    console.log('🔍 Testing auth session...');
    
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error) {
        console.error('❌ Session error:', error);
        return { success: false, error: error.message };
      }
      
      if (session) {
        console.log('✅ Active session found:', {
          userId: session.user.id,
          phone: session.user.phone,
          expiresAt: new Date(session.expires_at * 1000).toISOString()
        });
        return { 
          success: true, 
          session: {
            userId: session.user.id,
            phone: session.user.phone,
            expiresAt: session.expires_at
          }
        };
      } else {
        console.log('ℹ️ No active session');
        return { success: true, session: null };
      }
    } catch (err) {
      console.error('❌ Session check error:', err);
      return { success: false, error: err.message };
    }
  },

  // Test OTP send with detailed logging
  testOtpSend: async (phone) => {
    console.log('🔍 Testing OTP send to:', phone);
    const startTime = Date.now();
    
    // Normalize phone
    let normalizedPhone = phone.replace(/\D/g, '');
    if (!normalizedPhone.startsWith('1') && normalizedPhone.length === 10) {
      normalizedPhone = '1' + normalizedPhone;
    }
    normalizedPhone = '+' + normalizedPhone;
    
    console.log('📞 Normalized phone:', normalizedPhone);
    
    try {
      const { data, error } = await supabase.auth.signInWithOtp({
        phone: normalizedPhone,
        options: {
          channel: 'sms',
          shouldCreateUser: true
        }
      });
      
      const responseTime = Date.now() - startTime;
      
      if (error) {
        console.error('❌ OTP send failed:', error);
        
        // Parse error for more details
        if (error.message.includes('rate')) {
          return { 
            success: false, 
            error: 'Rate limited - too many attempts',
            responseTime 
          };
        }
        if (error.message.includes('provider')) {
          return { 
            success: false, 
            error: 'SMS provider (Twilio) error',
            responseTime 
          };
        }
        
        return { success: false, error: error.message, responseTime };
      }
      
      console.log(`✅ OTP sent successfully (${responseTime}ms)`);
      return { 
        success: true, 
        responseTime,
        message: `OTP sent in ${responseTime}ms`,
        data 
      };
    } catch (err) {
      console.error('❌ OTP send exception:', err);
      return { 
        success: false, 
        error: err.message,
        responseTime: Date.now() - startTime 
      };
    }
  },

  // Test OTP verification with detailed logging
  testOtpVerify: async (phone, token) => {
    console.log('🔍 Testing OTP verification...');
    const startTime = Date.now();
    
    // Normalize phone
    let normalizedPhone = phone.replace(/\D/g, '');
    if (!normalizedPhone.startsWith('1') && normalizedPhone.length === 10) {
      normalizedPhone = '1' + normalizedPhone;
    }
    normalizedPhone = '+' + normalizedPhone;
    
    console.log('📞 Phone:', normalizedPhone);
    console.log('🔑 Token:', token);
    
    // Create a promise that will resolve when auth state changes
    let authStateResolver;
    const authStatePromise = new Promise((resolve) => {
      authStateResolver = resolve;
    });
    
    // Set up auth state listener BEFORE verification
    const authListener = supabase.auth.onAuthStateChange((event, session) => {
      console.log(`🔐 Auth event during verification: ${event}`);
      if (event === 'SIGNED_IN' && session) {
        authStateResolver({ event, session });
      }
    });
    
    try {
      // Start verification (non-blocking)
      const verifyPromise = supabase.auth.verifyOtp({
        phone: normalizedPhone,
        token: token,
        type: 'sms'
      });
      
      // Race between verification promise and auth state change
      const result = await Promise.race([
        verifyPromise.then(r => ({ source: 'verify', ...r })),
        authStatePromise.then(r => ({ source: 'authState', ...r })),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Verification timeout')), 15000)
        )
      ]);
      
      const responseTime = Date.now() - startTime;
      
      console.log(`📊 Result source: ${result.source} (${responseTime}ms)`);
      
      if (result.source === 'authState') {
        console.log('✅ Verified via auth state change');
        return {
          success: true,
          responseTime,
          method: 'authStateChange',
          session: result.session
        };
      }
      
      if (result.error) {
        console.error('❌ Verification failed:', result.error);
        return { 
          success: false, 
          error: result.error.message,
          responseTime 
        };
      }
      
      console.log('✅ Verified via promise');
      return {
        success: true,
        responseTime,
        method: 'promise',
        data: result.data
      };
    } catch (err) {
      console.error('❌ Verification exception:', err);
      return { 
        success: false, 
        error: err.message,
        responseTime: Date.now() - startTime 
      };
    } finally {
      // Clean up listener
      authListener.data.subscription.unsubscribe();
    }
  },

  // Check user profile linkage
  checkUserProfile: async (userId) => {
    console.log('🔍 Checking user profile for:', userId);
    
    try {
      // Check by ID
      const { data: profileById } = await supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .single();
      
      if (profileById) {
        console.log('✅ Profile found by ID:', profileById);
        return { success: true, profile: profileById, method: 'id' };
      }
      
      // Get auth user for phone
      const { data: { user } } = await supabase.auth.getUser();
      if (user?.phone) {
        const phoneForDb = user.phone.replace('+', '');
        
        const { data: profileByPhone } = await supabase
          .from('users')
          .select('*')
          .eq('phone', phoneForDb)
          .single();
        
        if (profileByPhone) {
          console.log('✅ Profile found by phone:', profileByPhone);
          return { success: true, profile: profileByPhone, method: 'phone' };
        }
      }
      
      console.log('❌ No profile found');
      return { success: false, error: 'No profile found' };
    } catch (err) {
      console.error('❌ Profile check error:', err);
      return { success: false, error: err.message };
    }
  },

  // Run full diagnostic
  runFullDiagnostic: async () => {
    console.log('🏥 Running full authentication diagnostic...');
    console.log('=====================================');
    
    const results = {
      timestamp: new Date().toISOString(),
      connection: await authDiagnostics.testConnection(),
      session: await authDiagnostics.testAuthSession(),
      supabaseUrl: supabase.supabaseUrl,
      environment: process.env.NODE_ENV
    };
    
    // Check auth providers configuration
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        results.currentUser = {
          id: user.id,
          phone: user.phone,
          email: user.email,
          created: user.created_at
        };
        results.profile = await authDiagnostics.checkUserProfile(user.id);
      }
    } catch (err) {
      results.userCheckError = err.message;
    }
    
    console.log('📊 Diagnostic Results:', results);
    console.log('=====================================');
    
    return results;
  },

  // Test Twilio webhook
  testTwilioWebhook: async () => {
    console.log('🔍 Testing Twilio configuration...');
    
    // This would need to be implemented on the backend
    // For now, we'll check if SMS is configured in Supabase
    try {
      const testPhone = '+15555551234'; // Invalid number for testing
      
      const { error } = await supabase.auth.signInWithOtp({
        phone: testPhone,
        options: {
          channel: 'sms',
          shouldCreateUser: false
        }
      });
      
      if (error) {
        // Check error type
        if (error.message.includes('SMS Provider')) {
          return { 
            success: false, 
            error: 'Twilio not configured or credentials invalid' 
          };
        }
        if (error.message.includes('rate')) {
          return { 
            success: true, 
            message: 'Twilio is configured (rate limit reached)' 
          };
        }
        // If we get a phone validation error, Twilio is likely configured
        if (error.message.includes('phone') || error.message.includes('valid')) {
          return { 
            success: true, 
            message: 'Twilio appears to be configured' 
          };
        }
      }
      
      return { success: false, error: 'Unable to determine Twilio status' };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }
};

// Export diagnostic function for console usage
if (typeof window !== 'undefined') {
  window.authDiag = authDiagnostics;
  console.log('💡 Auth diagnostics available via window.authDiag');
  console.log('   Try: authDiag.runFullDiagnostic()');
}